import { useEffect, useState, useCallback, useRef } from 'react'
import { Plus, Trash2, Save, ShieldCheck } from 'lucide-react'
import toast from 'react-hot-toast'
import { useProjectStore } from '@/store/projectStore'
import Button from '@/components/common/Button'
import {
  type ProjectVariable, type ProjectVariableKind, type ProjectVariableTableSource,
  type OptionsPreview,
  listProjectVariables, saveProjectVariables, previewProjectVariableOptions,
} from '@/api/projectVariables'
import { getTables, getTableInfo } from '@/api/designers'

interface Props {
  /** True when the caller has the UPDATE_PROPERTIES permission. */
  canEdit?: boolean
  onAfterSave?: () => void
}

const NAME_RE = /^[a-z][a-z0-9_]{0,49}$/

function blankRow(): ProjectVariable {
  return { name: '', kind: 'value', value: '', description: '' }
}

/**
 * Project-level variables editor. Designed to live inside a Modal.
 * Variables can be referenced from a column's default_value as
 * `:<name>` (leading colon) and are resolved at INSERT time.
 */
export default function ProjectVariablesPanel({ canEdit = true, onAfterSave }: Props) {
  const { currentProject } = useProjectStore()
  const projectId = currentProject?.application.folder_name || ''

  const [loading, setLoading] = useState(true)
  const [rows, setRows] = useState<ProjectVariable[]>([])
  const [saving, setSaving] = useState(false)
  const [dirty, setDirty] = useState(false)

  const refresh = useCallback(async () => {
    if (!projectId) return
    setLoading(true)
    try {
      const list = await listProjectVariables(projectId)
      setRows(list)
      setDirty(false)
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Failed to load project variables', err)
      toast.error('Failed to load project variables')
    } finally {
      setLoading(false)
    }
  }, [projectId])

  useEffect(() => { refresh() }, [refresh])

  const update = (idx: number, patch: Partial<ProjectVariable>) => {
    setRows((rs) => rs.map((r, i) => i === idx ? { ...r, ...patch } : r))
    setDirty(true)
  }

  const addRow = () => {
    setRows((rs) => [...rs, blankRow()])
    setDirty(true)
  }

  const removeRow = (idx: number) => {
    setRows((rs) => rs.filter((_, i) => i !== idx))
    setDirty(true)
  }

  const validate = (): string | null => {
    const seen = new Set<string>()
    for (const r of rows) {
      const name = r.name.trim().toLowerCase()
      if (!NAME_RE.test(name)) {
        return `Invalid name "${r.name}". Use lowercase letters, digits, underscore — start with a letter (max 50 chars).`
      }
      if (seen.has(name)) {
        return `Duplicate variable name: "${name}".`
      }
      seen.add(name)
      if (r.kind === 'sql') {
        const v = r.value.trim().replace(/;\s*$/, '')
        if (!v) return `"${name}": SQL query is empty.`
        if (!/^select\b/i.test(v.replace(/^\(\s*/, ''))) {
          return `"${name}": SQL variable must start with SELECT.`
        }
      }
      if (r.kind === 'prompt' && (r.input_mode || 'input') === 'select') {
        if (r.source) {
          if (!r.source.table) return `"${name}": choose the table the list comes from.`
          if (!r.source.value_column) return `"${name}": choose the column holding the stored value.`
        } else {
          const v = r.value.trim().replace(/;\s*$/, '')
          if (!v) return `"${name}": choose the table the list comes from (or write its SQL).`
          if (!/^select\b/i.test(v.replace(/^\(\s*/, ''))) {
            return `"${name}": selection list must start with SELECT (column 1 = stored value, column 2 = label).`
          }
        }
      }
    }
    const inRights = rows.filter((r) => r.kind === 'prompt' && r.security_context)
    if (inRights.length > 1) {
      return `Only one variable can be used in the rights model (${inRights.map((r) => r.name).join(', ')}).`
    }
    return null
  }

  const save = async () => {
    const err = validate()
    if (err) { toast.error(err); return }
    setSaving(true)
    try {
      const saved = await saveProjectVariables(projectId, rows.map((r) => ({
        ...r,
        name: r.name.trim().toLowerCase(),
      })))
      setRows(saved)
      setDirty(false)
      toast.success('Project variables saved')
      onAfterSave?.()
    } catch (e) {
      const msg = (e as { response?: { data?: { detail?: string } } })
        ?.response?.data?.detail || 'Failed to save'
      toast.error(msg)
    } finally {
      setSaving(false)
    }
  }

  if (!currentProject) return null
  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 24 }}>
        <div className="spinner" />
      </div>
    )
  }

  return (
    <div>
      <p style={{ color: 'var(--color-text-secondary)', fontSize: '0.9125rem', marginBottom: 12 }}>
        Define variables that columns in transaction objects (and visual
        filters) can reference as their value using <code>:name</code> (leading
        colon). A variable can be a <strong>fixed value</strong>, a
        <strong> SQL query</strong> (first column of the first row), or
        <strong> Ask the user</strong> — prompted as soon as the user logs in,
        with an optional "Remember this" so it sticks per user. Bind params
        available in SQL: <code> :user_id</code>, <code> :user_email</code>,
        <code> :tenant_id</code>, <code> :client_ip</code>,
        <code> :project</code>.
      </p>

      {rows.length === 0 ? (
        <div style={{
          padding: 24, textAlign: 'center',
          color: 'var(--color-text-secondary)', fontSize: '0.9125rem',
          border: '1px dashed var(--color-border)', borderRadius: 8,
        }}>
          No project variables yet. They are optional — add one when a column's
          default needs to come from the logged-in user, site, or another
          project-wide value.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {rows.map((r, i) => (
            <VariableRow
              key={i}
              row={r}
              projectId={projectId}
              canEdit={canEdit}
              rightsTakenBy={rows.find((o, j) => j !== i && o.kind === 'prompt'
                && o.security_context)?.name || null}
              onChange={(p) => update(i, p)}
              onRemove={() => removeRow(i)}
            />
          ))}
        </div>
      )}

      {canEdit && (
        <div style={{
          display: 'flex', gap: 8, marginTop: 16,
          justifyContent: 'space-between', alignItems: 'center',
        }}>
          <Button variant="secondary" size="sm" icon={<Plus size={16} />} onClick={addRow}>
            Add variable
          </Button>
          <Button
            variant="primary" size="sm" icon={<Save size={16} />}
            disabled={!dirty || saving}
            onClick={save}
          >
            {saving ? 'Saving…' : dirty ? 'Save changes' : 'Saved'}
          </Button>
        </div>
      )}
    </div>
  )
}

function VariableRow({
  row, projectId, canEdit, rightsTakenBy, onChange, onRemove,
}: {
  row: ProjectVariable
  projectId: string
  canEdit: boolean
  /** Another variable already used in the rights model (only one may be). */
  rightsTakenBy: string | null
  onChange: (p: Partial<ProjectVariable>) => void
  onRemove: () => void
}) {
  return (
    <div style={{
      border: '1px solid var(--color-border)', borderRadius: 8,
      padding: 12, background: 'var(--color-bg-secondary)',
      display: 'grid', gap: 8,
      gridTemplateColumns: '1fr 140px 32px',
      alignItems: 'start',
    }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <label style={labelStyle}>Name (used as <code>:{row.name || 'name'}</code>)</label>
        <input
          style={inputStyle}
          value={row.name}
          disabled={!canEdit}
          onChange={(e) => onChange({ name: e.target.value })}
          placeholder="login_user"
        />
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <label style={labelStyle}>Kind</label>
        <select
          style={inputStyle}
          value={row.kind}
          disabled={!canEdit}
          onChange={(e) => onChange({ kind: e.target.value as ProjectVariableKind })}
        >
          <option value="value">Fixed value</option>
          <option value="sql">SQL query</option>
          <option value="prompt">Ask the user</option>
        </select>
      </div>
      <button
        type="button"
        onClick={onRemove}
        disabled={!canEdit}
        title="Remove variable"
        style={{
          marginTop: 22,
          background: 'transparent', border: 'none',
          cursor: canEdit ? 'pointer' : 'not-allowed',
          color: '#ef4444', padding: 4,
        }}
      >
        <Trash2 size={16} />
      </button>

      {row.kind === 'value' && (
        <div style={{ gridColumn: '1 / span 3', display: 'flex', flexDirection: 'column', gap: 4 }}>
          <label style={labelStyle}>Value</label>
          <input
            style={inputStyle}
            value={row.value}
            disabled={!canEdit}
            onChange={(e) => onChange({ value: e.target.value })}
            placeholder="HQ-01"
          />
        </div>
      )}

      {row.kind === 'sql' && (
        <div style={{ gridColumn: '1 / span 3', display: 'flex', flexDirection: 'column', gap: 4 }}>
          <label style={labelStyle}>SQL (single SELECT; first column of first row is used)</label>
          <textarea
            style={{ ...inputStyle, fontFamily: 'monospace', minHeight: 64 }}
            value={row.value}
            disabled={!canEdit}
            onChange={(e) => onChange({ value: e.target.value })}
            placeholder="SELECT email FROM users WHERE id = :user_id"
          />
        </div>
      )}

      {row.kind === 'prompt' && (
        <PromptFields row={row} projectId={projectId} canEdit={canEdit}
          rightsTakenBy={rightsTakenBy} onChange={onChange} />
      )}

      <div style={{ gridColumn: '1 / span 3', display: 'flex', flexDirection: 'column', gap: 4 }}>
        <label style={labelStyle}>Description (optional)</label>
        <input
          style={inputStyle}
          value={row.description}
          disabled={!canEdit}
          onChange={(e) => onChange({ description: e.target.value })}
          placeholder="Currently logged-in user's email"
        />
      </div>
    </div>
  )
}

function PromptFields({
  row, projectId, canEdit, rightsTakenBy, onChange,
}: {
  row: ProjectVariable
  projectId: string
  canEdit: boolean
  rightsTakenBy: string | null
  onChange: (p: Partial<ProjectVariable>) => void
}) {
  const mode = row.input_mode || 'input'
  const inRights = !!row.security_context
  return (
    <>
      <div style={{ gridColumn: '1 / span 3', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <label style={labelStyle}>How the user provides it</label>
          <select
            style={inputStyle}
            value={mode}
            disabled={!canEdit}
            onChange={(e) => {
              const m = e.target.value as 'input' | 'select'
              // A typed value cannot decide rights — leaving the list turns
              // "Use in rights model" off.
              onChange(m === 'input' ? { input_mode: m, security_context: false } : { input_mode: m })
            }}
          >
            <option value="input">Type a value</option>
            <option value="select">Pick from a list</option>
          </select>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <label style={labelStyle}>Default value (optional)</label>
          <input
            style={inputStyle}
            value={row.default || ''}
            disabled={!canEdit}
            onChange={(e) => onChange({ default: e.target.value })}
            placeholder="Used until the user sets one"
          />
        </div>
      </div>

      <div style={{ gridColumn: '1 / span 3', display: 'flex', flexDirection: 'column', gap: 4 }}>
        <label style={labelStyle}>Prompt shown to the user</label>
        <input
          style={inputStyle}
          value={row.prompt || ''}
          disabled={!canEdit}
          onChange={(e) => onChange({ prompt: e.target.value })}
          placeholder="Which branch are you working in?"
        />
      </div>

      {mode === 'select' && (
        <OptionsSourceEditor row={row} projectId={projectId} canEdit={canEdit} onChange={onChange} />
      )}

      {mode === 'select' && (
        <div style={{
          gridColumn: '1 / span 3', display: 'flex', flexDirection: 'column', gap: 6,
          padding: '10px 12px', borderRadius: 6,
          border: `1px solid ${inRights ? 'var(--color-accent, #2563eb)' : 'var(--color-border)'}`,
          background: inRights ? 'var(--color-bg, transparent)' : 'transparent',
        }}>
          <label style={{ ...checkboxRow, color: 'var(--color-text)', fontWeight: 600 }}
            title={rightsTakenBy ? `Only one variable can be used — "${rightsTakenBy}" already is.` : undefined}>
            <input
              type="checkbox"
              checked={inRights}
              disabled={!canEdit || (!!rightsTakenBy && !inRights)}
              onChange={(e) => onChange(e.target.checked
                ? { security_context: true, required: true }
                : { security_context: false })}
            />
            <ShieldCheck size={14} />
            Use in rights model
          </label>
          <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', lineHeight: 1.5 }}>
            {rightsTakenBy && !inRights ? (
              <>Already used by <code>{rightsTakenBy}</code> — an application has one.</>
            ) : inRights ? (
              <>
                Roles can now be given <b>per value</b> of this list (System
                administration → Users): a user may hold <i>Sales Clerk</i> in one value
                and nothing in another, and only sees the values they hold a role in.
                Their rights follow the value they pick. Rights decide what a user may
                <i> do</i>; to show only that value's records, add a row-security rule on
                each transaction such as <code>COMPANY = :{row.name || 'name'}</code>.
              </>
            ) : (
              <>Make the user's pick decide which of their roles apply — e.g. a
              Financial Entity, company or site with different rights in each.</>
            )}
          </div>
        </div>
      )}

      <div style={{ gridColumn: '1 / span 3', display: 'flex', gap: 18, flexWrap: 'wrap' }}>
        <label style={checkboxRow}
          title={inRights ? 'A value used in the rights model must always be answered.' : undefined}>
          <input
            type="checkbox"
            checked={!!row.required || inRights}
            disabled={!canEdit || inRights}
            onChange={(e) => onChange({ required: e.target.checked })}
          />
          Required (block the app until answered)
        </label>
        <label style={checkboxRow}>
          <input
            type="checkbox"
            checked={row.remember_default ?? true}
            disabled={!canEdit}
            onChange={(e) => onChange({ remember_default: e.target.checked })}
          />
          {inRights ? 'Remember the last choice (else ask at every sign-in)' : '"Remember this" ticked by default'}
        </label>
        <label style={checkboxRow}>
          <input
            type="checkbox"
            checked={!!row.external}
            disabled={!canEdit}
            onChange={(e) => onChange({ external: e.target.checked })}
          />
          Also ask on the external website
        </label>
      </div>
    </>
  )
}

/**
 * Where a pick-from-a-list variable's options come from: a TABLE chosen from
 * the project's tables (value column, label column, optional filter + sort —
 * the server compiles the SELECT), or custom SQL. A live preview runs the list
 * against the project's data so a wrong table or column shows up here.
 */
function OptionsSourceEditor({
  row, projectId, canEdit, onChange,
}: {
  row: ProjectVariable
  projectId: string
  canEdit: boolean
  onChange: (p: Partial<ProjectVariable>) => void
}) {
  // A new list starts "from a table"; one that already has SQL and no source
  // opens on that SQL so nothing an author wrote is hidden.
  const [fromTable, setFromTable] = useState(() => !!row.source || !row.value.trim())
  const src: ProjectVariableTableSource = row.source || { type: 'table', table: '', value_column: '' }
  const [tables, setTables] = useState<string[]>([])
  const [columns, setColumns] = useState<string[]>([])
  const [preview, setPreview] = useState<OptionsPreview | null>(null)
  const [previewing, setPreviewing] = useState(false)
  const seq = useRef(0)

  useEffect(() => {
    if (!projectId || !fromTable) return
    getTables(projectId)
      .then((res) => setTables(((res?.tables || []) as { name: string }[]).map((t) => t.name).sort()))
      .catch(() => setTables([]))
  }, [projectId, fromTable])

  useEffect(() => {
    if (!projectId || !fromTable || !src.table) { setColumns([]); return }
    const n = ++seq.current
    getTableInfo(projectId, src.table)
      .then((res) => {
        if (n !== seq.current) return
        const cols = Array.isArray(res?.table?.columns) ? res.table.columns : []
        setColumns(cols.map((c: { column_name: string }) => c.column_name).filter(Boolean))
      })
      .catch(() => { if (n === seq.current) setColumns([]) })
  }, [projectId, fromTable, src.table])

  const setSrc = (patch: Partial<ProjectVariableTableSource>) => {
    setPreview(null)
    onChange({ source: { ...src, ...patch, type: 'table' } })
  }

  const runPreview = async () => {
    setPreviewing(true)
    try {
      setPreview(await previewProjectVariableOptions(projectId,
        fromTable ? { source: src } : { value: row.value }))
    } catch (e) {
      const msg = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      setPreview({ ok: false, options: [], sql: '', error: msg || 'Preview failed' })
    } finally {
      setPreviewing(false)
    }
  }

  return (
    <div style={{ gridColumn: '1 / span 3', display: 'flex', flexDirection: 'column', gap: 6 }}>
      <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
        <label style={labelStyle}>Options come from</label>
        <label style={checkboxRow}>
          <input type="radio" checked={fromTable} disabled={!canEdit}
            onChange={() => { setPreview(null); setFromTable(true); onChange({ source: src }) }} />
          A table
        </label>
        <label style={checkboxRow}>
          <input type="radio" checked={!fromTable} disabled={!canEdit}
            onChange={() => { setPreview(null); setFromTable(false); onChange({ source: null, value: row.value || '' }) }} />
          Custom SQL
        </label>
      </div>

      {fromTable ? (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <label style={labelStyle}>Table</label>
            <select style={inputStyle} value={src.table} disabled={!canEdit}
              onChange={(e) => setSrc({ table: e.target.value, value_column: '', label_column: '', order_by: '' })}>
              <option value="">— Choose a table —</option>
              {src.table && !tables.includes(src.table) && <option value={src.table}>{src.table}</option>}
              {tables.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <label style={labelStyle}>Stored value column</label>
            <select style={inputStyle} value={src.value_column} disabled={!canEdit || !src.table}
              onChange={(e) => setSrc({ value_column: e.target.value })}>
              <option value="">— Choose —</option>
              {src.value_column && !columns.includes(src.value_column)
                && <option value={src.value_column}>{src.value_column}</option>}
              {columns.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <label style={labelStyle}>Label shown to the user</label>
            <select style={inputStyle} value={src.label_column || ''} disabled={!canEdit || !src.table}
              onChange={(e) => setSrc({ label_column: e.target.value })}>
              <option value="">(same as the value)</option>
              {src.label_column && !columns.includes(src.label_column)
                && <option value={src.label_column}>{src.label_column}</option>}
              {columns.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div style={{ gridColumn: '1 / span 2', display: 'flex', flexDirection: 'column', gap: 4 }}>
            <label style={labelStyle}>Only rows where (optional)</label>
            <input style={inputStyle} value={src.filter || ''} disabled={!canEdit || !src.table}
              placeholder="status = 'A'"
              onChange={(e) => setSrc({ filter: e.target.value })} />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <label style={labelStyle}>Sort by</label>
            <select style={inputStyle} value={src.order_by || ''} disabled={!canEdit || !src.value_column}
              onChange={(e) => setSrc({ order_by: e.target.value })}>
              <option value="">Label</option>
              {src.value_column && <option value={src.value_column}>{src.value_column} (value)</option>}
            </select>
          </div>
        </div>
      ) : (
        <>
          <label style={labelStyle}>
            Selection list SQL — column 1 = stored value, column 2 = label shown
          </label>
          <textarea
            style={{ ...inputStyle, fontFamily: 'monospace', minHeight: 64 }}
            value={row.value}
            disabled={!canEdit}
            onChange={(e) => { setPreview(null); onChange({ value: e.target.value }) }}
            placeholder="SELECT branch_code, branch_name FROM branches ORDER BY branch_name"
          />
        </>
      )}

      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <button type="button" onClick={runPreview}
          disabled={previewing || (fromTable ? !(src.table && src.value_column) : !row.value.trim())}
          style={{
            padding: '4px 10px', borderRadius: 4, fontSize: '0.8125rem', cursor: 'pointer',
            border: '1px solid var(--color-border)', background: 'transparent', color: 'var(--color-text)',
          }}>
          {previewing ? 'Checking…' : 'Preview the list'}
        </button>
        {preview && (
          <div style={{ flex: 1, minWidth: 220, fontSize: '0.8125rem', lineHeight: 1.5,
            color: preview.ok ? 'var(--color-text-secondary)' : 'var(--color-error, #b91c1c)' }}>
            {preview.ok ? (
              preview.options.length === 0 ? 'The list is empty — add rows to the table.' : (
                <>
                  {preview.total} value(s):{' '}
                  {preview.options.slice(0, 8).map((o) => (o.label && o.label !== String(o.value)
                    ? `${o.label} (${o.value})` : String(o.value))).join(', ')}
                  {(preview.total || 0) > 8 ? ', …' : ''}
                </>
              )
            ) : preview.error}
          </div>
        )}
      </div>
    </div>
  )
}

const labelStyle: React.CSSProperties = {
  fontSize: '0.8125rem',
  color: 'var(--color-text-secondary)',
  fontWeight: 500,
  letterSpacing: 0.2,
}

const checkboxRow: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 6,
  fontSize: '0.8425rem', color: 'var(--color-text-secondary)', cursor: 'pointer',
}

const inputStyle: React.CSSProperties = {
  padding: '7px 10px',
  borderRadius: 4,
  border: '1px solid var(--color-border)',
  background: 'var(--lp-input-bg)',
  color: 'var(--color-text)',
  fontSize: '0.9125rem',
  outline: 'none',
  width: '100%',
  boxSizing: 'border-box',
}
